import { Component, OnInit, OnDestroy, ViewChildren, QueryList, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, Subscription, Observable, combineLatest, of, forkJoin } from "rxjs";
import { finalize, tap, switchMap, concatMap, catchError } from "rxjs/operators";
import { CanDeactivateComponent } from "../../../common/classes/can-deactivate.component";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { AuthService } from "../../../services/auth.service";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { SubHeader } from "../../../core/models/sub-header.model";
import { PaymentService } from "../../../payments/services/payment.service";
import { PaymentRecordAdjustmentRequestSearchSpec, ApplicationPayment, PaymentRecordAdjustmentNote, PaymentSearchCriteria, PaymentRecordAdjustment, PaymentRecordAdjustmentRequest, AdjustmentRequestStatus, PaymentStatus, Payment, PaymentReceivedInformation } from "../../../payments/models/paymentItem.model";
import { IUploadFile, DocumentManagementService } from '../../../common/services/document-management.service';
import { parseFileNameFromHeaders } from "../../../common/models/onBaseIntegrationAndMapping.model";
import { LoaderService } from "../../../common/services/loader.service";
import { ReapplicationReceiptComponent } from "../../../common/components/reapplication-receipt/reapplication-receipt.component";
import { FlowInstanceService } from "../../../flow/services/flow-instance.service";
import { PaymentReceivedDateRequest } from "../../../flow/models/flow.model";

@Component({
    selector: 'app-approve-payment-adjustment',
    templateUrl: './approve-payment-adjustment.component.html',
    styleUrls: ['./approve-payment-adjustment.component.scss'],
    standalone: false
})
export class ApprovePaymentAdjustmentComponent extends CanDeactivateComponent implements OnInit, OnDestroy {

    get applicationList(): string[] {
        let result: string[] = [];
        if (this.fromApplications != null) this.fromApplications.forEach(f => result.push(f));
        if (this.toApplications != null) this.toApplications.forEach(t => result.push(t));
        return result;
    }
    applicationPayments: Map<string, ApplicationPayment[]> = new Map<string, ApplicationPayment[]>();

    get canApprove(): boolean {
        if (this.request == null) return false;
        return this.request.requestStatus == AdjustmentRequestStatus.Pending && this.auth.User.id !== this.request.requestedById;
    }

    get cantApproveText(): string {
        if (this.request == null) return "";
        if (this.request.requestStatus == AdjustmentRequestStatus.Approved) return this.alreadyApproved;
        if (this.request.requestStatus == AdjustmentRequestStatus.Denied) return this.alreadyDenied;
        return this.sameAsRequestor;
    }
    alreadyApproved: string = "Adjustment request has already been approved.";
    alreadyDenied: string = "Adjustment request has already been denied.";
    sameAsRequestor: string = "An individual who submitted a request to adjust payments cannot also approve that request.";
    dataLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    documentation: IUploadFile[] = [];
    @ViewChild('downloadLink', { static: true }) public downloadLink: ElementRef;
    

    fromApplications: string[] = [];
    fromPayments: string[] = [];

    get notes(): PaymentRecordAdjustmentNote[] {
        if (this.request == null) return [];
        return this.request.paymentRecordAdjustmentNotes;
    }

    set notes(value: PaymentRecordAdjustmentNote[]) {
        if (this.request == null) return;
        this.request.paymentRecordAdjustmentNotes = value;
    }

    @ViewChildren(ReapplicationReceiptComponent) receipts: QueryList<ReapplicationReceiptComponent>;

    receivedInformation: PaymentReceivedInformation[] = [];
    request: PaymentRecordAdjustmentRequest;

    private requestId: string;
    resultDialog: Dialog = new Dialog("Request #[XXX] has been [YYY]", [new DialogButton("OK", true, () => { this.home(); })]);
    saveInProgress: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    statuses = AdjustmentRequestStatus;
    private subheader: SubHeader = new SubHeader({ title: "Approve Payment Adjustment - [XXX]" });
    private subscription: Subscription = new Subscription();

    toApplications: string[] = [];

    unpaidStatuses: any[] = [PaymentStatus.Pending, PaymentStatus.Overdue, PaymentStatus.Partial];

    constructor(dialogService: DynamicDialogService, auth: AuthService, private route: ActivatedRoute, private router: Router, private subheaderService: SubHeaderService,
        private paymentService: PaymentService, private loader: LoaderService, private documentService: DocumentManagementService, private flowService: FlowInstanceService) {
        super(dialogService, auth);
    }

    ngOnInit() {
        this.confirmDialog = new Dialog("Any unsubmitted changes will be lost upon leaving this page.  Do you wish to continue?", [new DialogButton("Yes", true, () => { this.allowDeactivate.next(true); }), new DialogButton("No", false, () => { return false; })]);
        this.requestId = this.route.snapshot.params.id;
        this.subheader.title = this.subheader.title.replace("[XXX]", this.requestId);
        this.subheaderService.updateSubHeader(this.subheader);
        let spec: PaymentRecordAdjustmentRequestSearchSpec = {
            requestId: this.requestId
        };
        let dataObs$ = this.paymentService.findAdjustmentRequest(this.requestId).pipe(
            tap((obj: PaymentRecordAdjustmentRequest) => {
                if (obj != null && obj.paymentRecordAdjustmentDocuments != null) {
                    this.documentation = [];
                    obj.paymentRecordAdjustmentDocuments.forEach(d => {
                        let file: IUploadFile = {
                            rawFile: null,
                            completed: true,
                            canBeDeleted: false,
                            sentToOnbase: false,
                            error: false,
                            isUploading: false,
                            waiting: false,
                            percentage: 100,
                            path: d.blobUri,
                            fileName: d.fileName,
                            type: d.type,
                            uploadedByUserId: d.uploadedById,
                            blobUri: d.blobUri,
                            documentType: d.onBaseDocumentType
                        };
                        this.documentation.push(file);
                    });
                }
                this.request = obj;
            }),
            switchMap((req: PaymentRecordAdjustmentRequest) => {
                let obs$: Observable<ApplicationPayment[]> = of([]);
                if (req.paymentRecordAdjustments != null && req.paymentRecordAdjustments.length > 0) {                    
                    for (let a = 0; a < req.paymentRecordAdjustments.length; a++) {
                        let adj = req.paymentRecordAdjustments[a];
                        if (this.fromApplications.includes(adj.fromApplicationNumber) == false) this.fromApplications.push(adj.fromApplicationNumber);
                        
                        if (this.toApplications.includes(adj.toApplicationNumber) == false) this.toApplications.push(adj.toApplicationNumber);
                    }
                    let obses$: Observable<ApplicationPayment[]>[] = [];
                    this.fromApplications.forEach(a => {
                        let spec = new PaymentSearchCriteria("", a, [], []);
                        let appObs$ = this.paymentService.search(spec).pipe(
                            tap((pmts: ApplicationPayment[]) => {
                                pmts.forEach(p => {
                                    let index = p.paymentRecords.findIndex(p => req.paymentRecordAdjustments.find(adj => adj.paymentRecordId == p.id)); //should only ever be one since paymentRecordId is a primary key
                                    if (index != -1) {
                                        let record = p.paymentRecords[index];
                                        p.amountPaid -= record.amount;
                                        p.amountDue += record.amount;
                                        p.paymentStatus = p.amountPaid > 0 ? PaymentStatus.Partial : PaymentStatus.Pending;
                                        p.paymentRecords.splice(index, 1);
                                    }
                                    if (p.paymentReceivedDate != null) {
                                        let pmtInfo: PaymentReceivedInformation = {
                                            applicationPaymentId: p.id,
                                            paymentReceivedDate: p.paymentReceivedDate,
                                            paymentReceivedById: p.paymentReceivedById,
                                            paymentReceivedByName: p.paymentReceivedByName
                                        } as PaymentReceivedInformation;
                                        this.receivedInformation.push(pmtInfo);
                                    }
                                    if (p.paymentStatus != PaymentStatus.Paid) {
                                        p.paymentReceivedDate = null;
                                        p.paymentReceivedById = null;
                                        p.paymentReceivedByName = null;
                                    }
                                });
                                this.applicationPayments.set(a, pmts);
                            })
                        );
                        obses$.push(appObs$);
                    });
                    this.toApplications.forEach(a => {
                        let spec = new PaymentSearchCriteria("", a, [], []);
                        let appObs$ = this.paymentService.search(spec).pipe(
                            tap((pmts: ApplicationPayment[]) => {
                                pmts.forEach(p => {
                                    let applicableAdjustments = req.paymentRecordAdjustments.filter(adj => adj.toApplicationPaymentId === p.id);
                                    if (applicableAdjustments.length > 0) {
                                        //create a new payment record on the application payment for each adjustment, and adjust its amount paid, amount due and status accordingly
                                        applicableAdjustments.forEach(adj => {
                                            let payment: Payment = {
                                                applicationPaymentId : p.id,
                                                amount: adj.amountApplied,
                                                instrumentAmount: adj.instrumentAmount,
                                                paymentType: adj.paymentType,
                                                paymentDate: adj.paymentDate,                                                
                                                token: adj.instrumentToken,
                                                tokenId: adj.tokenId
                                            };
                                            if (p.paymentRecords == null) p.paymentRecords = [];
                                            p.paymentRecords.push(payment);
                                            p.amountDue -= adj.amountApplied;
                                            p.amountPaid += adj.amountApplied;
                                            p.paymentStatus = (p.amountDue == 0) ? PaymentStatus.Paid : PaymentStatus.Partial;
                                        });
                                        if (p.paymentStatus == PaymentStatus.Paid) { //find the first application a payemnt came from that matches this to, and move its payment info
                                            let adj: PaymentRecordAdjustment = req.paymentRecordAdjustments.find(adj => adj.toApplicationNumber == a);
                                            if (adj != null) {
                                                let receivedInfo = this.receivedInformation.find(info => info.applicationPaymentId == adj.fromApplicationPaymentId);
                                                if (receivedInfo != null) {
                                                    p.paymentReceivedDate = receivedInfo.paymentReceivedDate;
                                                    p.paymentReceivedById = receivedInfo.paymentReceivedById;
                                                    p.paymentReceivedByName = receivedInfo.paymentReceivedByName;
                                                    p.paymentRecords.forEach(r => {
                                                        r.paymentReceivedDate = receivedInfo.paymentReceivedDate;
                                                        r.paymentReceivedById = receivedInfo.paymentReceivedById;
                                                        r.paymentReceivedByName = receivedInfo.paymentReceivedByName;
                                                    })
                                                }
                                            }
                                        }
                                    }
                                })
                                this.applicationPayments.set(a, pmts);
                            })
                        );
                        obses$.push(appObs$);
                    });
                    if (obses$.length > 0) {
                        obs$ = obses$[0];
                        for (let o = 1; o < obses$.length; o++) {
                            obs$ = obs$.pipe(
                                concatMap((pmts: ApplicationPayment[]) => {
                                    return obses$[o];
                                })
                            );
                        }
                    }
                }
                return obs$;
            }),
            finalize(() => {
                this.dataLoading.next(false);
            })
        ).subscribe(
            (data: any) => {
            },
            (error) => {
                console.error(error);
                this.dialogService.showError();
            }
        );
        this.subscription.add(dataObs$);
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    addNote(note: PaymentRecordAdjustmentNote): void {
        if (this.notes == null) this.notes = [];
        this.notes.push(note);
    }


    approve(): void {
        //need to update the request as approved through a savepaymentadjustmentrequest call
        this.saveInProgress.next(true);
        this.request.requestStatus = AdjustmentRequestStatus.Approved;
        let saveObs$ = this.saveObservable();
        //need to ensure that all adjusted payments get saved
        let paymentsObs$: Observable<ApplicationPayment>[] = [];
        for (let a = 0; a < this.applicationList.length; a++) {
            let payments = this.getPaymentsByApplication(this.applicationList[a]);            
            for (let p = 0; p < payments.length; p++) {
                let pmtObs$: Observable<ApplicationPayment> = this.paymentService.savePaymentRecord(payments[p]);
                paymentsObs$.push(pmtObs$);
            }
        }
         //need to ensure all invoices get generated
        let receiptsObs$: Observable<string>[] = [];
        this.receipts.forEach(r => {
            let receiptObs$: Observable<string> = (<ReapplicationReceiptComponent>r).generatePdf().pipe(
                catchError((error) => {
                    console.error(error);
                    this.dialogService.showError();
                    return of("");
                }
            ));
            receiptsObs$.push(receiptObs$)
        })
        this.loader.toggleLoader(true);
        let obs$: Observable<any> = saveObs$.pipe(
            switchMap((obj: PaymentRecordAdjustmentRequest) => {
                return combineLatest(paymentsObs$);
            }),
            switchMap((pmts: ApplicationPayment[]) => {
                return forkJoin(receiptsObs$).pipe(
                    concatMap((pdfs: string[]) => {
                        let download$: Observable<ApplicationPayment>[] = [];
                        pdfs.forEach(p => {
                            let pdf$ = this.documentService.downloadFromQueue(p).pipe(
                                switchMap((data: any) => {
                                    const fileName = parseFileNameFromHeaders(data.headers.get("content-disposition"));
                                    const blob = new Blob([data.body], { type: data.body.type });
									const blobUri = window.URL.createObjectURL(blob);
									const anchor = <HTMLAnchorElement>this.downloadLink.nativeElement;
									anchor.href = blobUri;
									anchor.download = fileName;
									anchor.click();
                                    let app: string = "";
                                    let fileNameParts = fileName.split("_");
                                    if (fileNameParts.length == 2) {
                                        app = fileNameParts[1].replace('.pdf', '');

                                    }
                                    let resp$: Observable<ApplicationPayment> = of(null);
                                    if (app != null) {
                                        let pmt = pmts.find(p => p.applicationNumber == app);
                                        if (pmt != null) {
                                            if (pmt.paymentStatus == PaymentStatus.Paid) pmt.queuedReceipt = p;
                                            else pmt.queuedInvoice = p;
                                            resp$ = this.paymentService.savePaymentRecord(pmt); //need to re-save the payment to associate the queued document to it
                                        }
                                       
                                    }
                                    return resp$;
                                })
                            );
                            download$.push(pdf$);
                        });
                        return combineLatest(download$);
                    })
                )
            }),
            switchMap((pmts: ApplicationPayment[]) => {
                let applications: string[] = [];
                pmts.forEach(p => {
                    if (applications.includes(p.applicationNumber) === false) applications.push(p.applicationNumber);
                });
                let obs$: Observable<any>[] = [];
                applications.forEach(a => {
                    let criteria = new PaymentSearchCriteria("", a, [], []);
                    let pmtObs$ = this.paymentService.search(criteria).pipe(
                        switchMap((appPmts: ApplicationPayment[]) => {
                            let updateObs$: Observable<any> = of(null);
                            if (appPmts != null && appPmts.length > 0) {
                                let req: PaymentReceivedDateRequest = null;
                                if (appPmts.find(p => p.paymentStatus != PaymentStatus.Paid) == null) {
                                    req = {
                                        applicationNumbers: [appPmts[0].applicationNumber],
                                        paymentReceivedDate: appPmts[0].paymentReceivedDate,
                                        isElectronicPayment: false
                                    } as PaymentReceivedDateRequest;

                                } else {
                                    req = {
                                        applicationNumbers: [appPmts[0].applicationNumber],
                                        isElectronicPayment: false
                                    } as PaymentReceivedDateRequest; //omitting payment received date to reset it
                                }
                                updateObs$ = this.flowService.setPaymentReceivedDates(req); 
                            }                           
                            return updateObs$;
                        })
                    );
                    obs$.push(pmtObs$);
                });
                return combineLatest(obs$);
            }),
            finalize(() => {
                this.loader.toggleLoader(false);
                this.saveInProgress.next(false);
            })
        );
        let subs$ = obs$.subscribe(
            (data: any) => {
                this.resultDialog.message = this.resultDialog.message.replace("[XXX]", this.request.requestId);
                this.resultDialog.message = this.resultDialog.message.replace("[YYY]", "approved.");
                this.resultDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.resultDialog);
            },
            (error) => {
                this.logError(error);
            }
        );
        this.subscription.add(subs$);

    }

    deleteNote(index: number): void {
        if (this.notes == null || index > this.notes.length || index === -1) return;
        this.notes.splice(index, 1);
    }

    deny(): void {
        this.saveInProgress.next(true);
        this.request.requestStatus = AdjustmentRequestStatus.Denied;
        this.loader.toggleLoader(true);
        let obs$ = this.saveObservable().pipe(
            finalize(() => {
                this.loader.toggleLoader(false);
                this.saveInProgress.next(false);
            })
        ).subscribe(
            () => {
                this.resultDialog.message = this.resultDialog.message.replace("[XXX]", this.request.requestId);
                this.resultDialog.message = this.resultDialog.message.replace("[YYY]", "denied.");
                this.resultDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.resultDialog);
            },
            (error) => {
                console.error(error);
                this.dialogService.showError();
            }
        );
        this.subscription.add(obs$);
    }

    getAdjustmentsForApplication(application: string): PaymentRecordAdjustment[] {
        if (this.request == null || this.request.paymentRecordAdjustments == null) return [];
        return this.request.paymentRecordAdjustments.filter(p => p.fromApplicationNumber == application || p.toApplicationNumber == application);
    }

    getPaymentsByApplication(application: string): ApplicationPayment[] {
        return this.applicationPayments.get(application);
    }

    home(): void {
        this.allowDeactivate.next(true);
        this.router.navigate(["/home"]);
    }

    logError(err: any): void {
        console.error(err);
        this.dialogService.showError();
    }

    receiptIsPaid(application: string): boolean {
        let payments = this.getPaymentsByApplication(application);
        return payments != null && payments.length > 0 && payments.find(p => p.paymentStatus === PaymentStatus.Paid || p.paymentStatus === PaymentStatus.Partial) != null;
    }

    save(): void {
        this.loader.toggleLoader(true);
        this.saveInProgress.next(true);
        let obs$ = this.saveObservable().pipe(
            finalize(() => {
                this.loader.toggleLoader(false);
                this.saveInProgress.next(false);
            })
        ).subscribe(
            () => {
                this.resultDialog.message = this.resultDialog.message.replace("[XXX]", this.request.requestId);
                this.resultDialog.message = this.resultDialog.message.replace("[YYY]", "saved");
                this.resultDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.resultDialog);
            },
            (error) => {
                console.error(error);
                this.dialogService.showError();
            }
        );
        this.subscription.add(obs$);
    }

    saveObservable(): Observable<PaymentRecordAdjustmentRequest> {
        return this.paymentService.savePaymentAdjustment(this.request);
        
    }
}
